import axios from "axios";
import { SsoApiUrl } from "./ApiUrl";
import { ls_vars } from "Library/Constants";

const timeout = process.env.NODE_ENV === "development" ? 300000 : 120000;

const apiClient = axios.create({
  timeout,
  headers: { "Content-Type": "application/json", Accept: "*/*" },
  withCredentials: false,
  responseType: "json",
});

type HeadersType = {
  Authorization?: string;
  "Content-Type"?: string;
};

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    switch (error.response.status) {
      case 401:
        if (error.response.data?.code === "incomplete_onboarding") {
          return (window.location.href = SsoApiUrl.logoutPage);
        } else {
          return refreshTokenRequest().then((access) => {
            if (access) {
              return axios.request({
                ...error.config,
                headers: {
                  ...error.config.headers,
                  Authorization: `JWT ${access}`,
                },
              });
            }
          });
        }
      default:
        break;
    }

    return Promise.reject(error);
  }
);

export const setSession = (token?: string | null) => {
  if (token) {
    const JWT = `JWT ${token}`;
    apiClient.defaults.headers.common.Authorization = JWT;
  } else {
    delete apiClient.defaults.headers.common.Authorization;
  }
};

export const overSession = () => {
  localStorage.clear();
  window.location.href = SsoApiUrl.logoutPage;
};

export const refreshTokenRequest = async () => {
  const refreshLocal = getLocalStorage(ls_vars.REFRESH_TOKEN);
  if (refreshLocal) {
    return await axios
      .post(SsoApiUrl.tokenRefresh, { refresh: refreshLocal })
      .then((res) => {
        const { access } = res.data;
        if (access) {
          setSession(access);
          setLocalStorage(ls_vars.ACCESS_TOKEN, access);
          return access;
        }
      })
      .catch(overSession);
  } else {
    overSession();
    return null;
  }
};

export const setLocalStorage = (tokenName: string, tokenValue: any) => {
  const unacceptableValues = ["undefined", "null", "NaN", null, undefined, NaN];
  
  if (!unacceptableValues.includes(tokenValue)) {
    localStorage.setItem(tokenName, tokenValue);
    
    // Verify the data was saved correctly
    const savedData = localStorage.getItem(tokenName);
  }
};

export const getLocalStorage = (tokenName: string) => {
  const item = localStorage.getItem(tokenName);
  
  try {
    if (item) {
      const parsedItem = JSON.parse(item);
      return parsedItem;
    } else {
      return null;
    }
  } catch (error) {
    return localStorage.getItem(tokenName);
  }
};

const tokenHeaders = async (): Promise<{ headers: HeadersType }> => {
  const accessToken = getLocalStorage(ls_vars.ACCESS_TOKEN);
  const headers: HeadersType =
    accessToken !== null ? { Authorization: `JWT ${accessToken}` } : {};
  return { headers };
};

export const get = async (url: string, params?: Record<string, any>) => {
  const queryParams = params || {};
  const projectRef = getLocalStorage(ls_vars.PROJECT_REF);
  if (projectRef && !queryParams.project) queryParams.project = projectRef;
  const headers = await tokenHeaders();
  const response = await apiClient.get(url, {
    ...headers,
    params: queryParams,
  });
  return response;
};

export const getFile = async (url: string, params?: Record<string, any>) => {
  const queryParams = params || {};
  const projectRef = getLocalStorage(ls_vars.PROJECT_REF);
  if (projectRef && !queryParams.project) queryParams.project = projectRef;
  const headers = tokenHeaders();
  const response = await apiClient.get(url, {
    responseType: "blob",
    ...headers,
    params: queryParams,
  });
  return response;
};

export const post = async (
  url: string,
  data: any,
  params?: Record<string, any>
) => {
  const queryParams = params || {};
  const projectRef = getLocalStorage(ls_vars.PROJECT_REF);
  if (projectRef && !queryParams.project) queryParams.project = projectRef;
  const headers = await tokenHeaders();

  // Update Content-Type header if data is FormData
  if (data instanceof FormData) {
    headers.headers["Content-Type"] = "multipart/form-data";
  }

  const response = await apiClient.post(url, data, {
    ...headers,
    params: queryParams,
  });
  return response;
};

export const patch = async (
  url: string,
  data: any,
  params?: Record<string, any>
) => {
  const queryParams = params || {};
  const projectRef = getLocalStorage(ls_vars.PROJECT_REF);
  if (projectRef && !queryParams.project) queryParams.project = projectRef;
  const headers = await tokenHeaders();

  // Update Content-Type header if data is FormData
  if (data instanceof FormData) {
    headers.headers["Content-Type"] = "multipart/form-data";
  }

  const response = await apiClient.patch(url, data, {
    ...headers,
    params: queryParams,
  });
  return response;
};

export const put = async (
  url: string,
  data: any,
  params?: Record<string, any>
) => {
  const queryParams = params || {};
  const projectRef = getLocalStorage(ls_vars.PROJECT_REF);
  if (projectRef && !queryParams.project) queryParams.project = projectRef;
  const headers = await tokenHeaders();

  // Update Content-Type header if data is FormData
  if (data instanceof FormData) {
    headers.headers["Content-Type"] = "multipart/form-data";
  }

  const response = await apiClient.put(url, data, {
    ...headers,
    params: queryParams,
  });
  return response;
};
// Note: delete is a reserved word
export const remove = async (url: string, params?: Record<string, any>) => {
  const queryParams = params || {};
  const projectRef = getLocalStorage(ls_vars.PROJECT_REF);
  if (projectRef && !queryParams.project) queryParams.project = projectRef;
  const headers = await tokenHeaders();
  const response = await apiClient.delete(url, {
    ...headers,
    params: queryParams,
  });
  return response;
};
